import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { SwivuList } from "@/modern/components/swivu-list";
import { TAGS_KEYS } from "@/other/constants/TagsKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useState, useEffect, useMemo } from "react";
import { TagValue } from "@/bryntum-lib/models/TagValue";
import { toast } from "sonner";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { STORE_KEYS } from "@/other/constants/StoreKeys";

const ContactTagDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user } = useSwivuBryntumContext();
  const [tags, setTags] = useState<TagValue[]>([]);

  const canEdit = useMemo<boolean>(() => {
    if (!user) return false;
    return user.can(GRANTS.grant_one);
  }, [user])

  const { subscribe } = useSwivuStore({
    storeId: STORE_KEYS.contactTagValuesStore,
  })

  const loadTags = () => {
    if (!project) return;
    const tagValueStore = project.contactTagValues;
    const tagRecords = tagValueStore.query((record: TagValue) => record.tagSet === TAGS_KEYS.tag, true) || [];
    setTags(() => tagRecords);
  }

  useEffect(() => {
    loadTags();
    return subscribe(() => {
      loadTags();
    })
  }, [subscribe]);

  const isDuplicateTagName = (name: string, excludeTagId?: string | number) => {
    if (!project) return false;
    const tagValueStore = project.contactTagValues;
    const allTags = tagValueStore.query((record: TagValue) => record.tagSet === TAGS_KEYS.tag, true) || [];
    return allTags.some(t => {
      if (excludeTagId !== undefined && t.id === excludeTagId) {
        return false;
      }
      return t.tagValue.toLowerCase() === name.toLowerCase();
    });
  };

  const handleAddItem = (value: string) => {
    if (!value.trim()) {
      return;
    }

    if (isDuplicateTagName(value)) {
      toast.error('Tag name already exists');
      return;
    }

    if (!project) return;
    const tagValueStore = project.contactTagValues;
    const newTag = tagValueStore.createRecord({
      tagSet: TAGS_KEYS.tag,
      tagValue: value,
    });
    tagValueStore.add(newTag);
  }

  const handleReorderItems = (movedItem: TagValue, beforeItem: TagValue | null) => {
    if (!project) return;
    const tagValueStore = project.contactTagValues;
    // @ts-expect-error - tagValue is a TagValue model
    tagValueStore.move(movedItem, beforeItem);
  }

  const handleUpdateTagName = (tag: TagValue, newName: string) => {
    if (!newName.trim()) {
      return false;
    }

    if (isDuplicateTagName(newName, tag.id)) {
      toast.error('Tag name already exists');
      return false;
    }

    tag.tagValue = newName;
    return true;
  }

  const handleDeleteTag = (tag: TagValue) => {
    if (!project) return;
    const tagValueStore = project.contactTagValues;
    // @ts-expect-error - tag is a TagValue model
    tagValueStore.remove(tag);
  }

  return (
    <div className="p-6 flex flex-col max-w-3xl w-full h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <SwivuList.Root<TagValue>
        items={tags}
        onReorder={handleReorderItems}
        searchConfig={{
          fields: ["tagValue"],
          placeholder: "Search tags...",
        }}
      >
        <SwivuList.Toolbar />

        <SwivuList.Content<TagValue> emptyMessage="No tags yet. Create your first tag!">
          {(tag: TagValue) => (
            <SwivuList.Item value={tag.id as string | number}>
              {/* Drag Handle */}
              <SwivuList.ItemHandle disabled={!canEdit} value={tag.id as string | number} />

              {/* Tag Name */}
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <SwivuList.TextField
                  item={tag}
                  value={tag.tagValue}
                  onUpdate={handleUpdateTagName}
                  placeholder="Enter tag name"
                  disabled={!canEdit}
                  className="flex-1"
                />
              </div>

              {/* Actions */}
              <SwivuList.ItemActions
                disabled={!canEdit}
                onDelete={() => handleDeleteTag(tag)}
              />
            </SwivuList.Item>
          )}
        </SwivuList.Content>

        <SwivuList.QuickAdd
          disabled={!canEdit}
          onAdd={handleAddItem}
          placeholder="Add new tag..."
        />
      </SwivuList.Root>
    </div>
  );
};

export default ContactTagDetail;
